import { Component, ElementRef, HostBinding, HostListener, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { DashboardAction } from '../../models/DashboardAction';
import { DashboardComponent } from '../../models/DashboardComponent';
import { ExDashboardEventService } from '../../services/event.service';

@Component({
    selector: 'gtw-ex-component-container',
    templateUrl: './ex-component-container.component.html',
    styleUrls: ['./ex-component-container.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ExComponentContainerComponent implements OnInit {

    @Input()
    title: string = '';

    @Input()
    height: string = '450px';

    @Input()
    actions: DashboardAction[] = [];

    @Input()
    component!: DashboardComponent;

    /**
     * Show the legacy fullscreen/resize icon (`fa fa-expand` / `fa fa-times`).
     * Defaults to `true` to preserve admin executive dashboard parity, but
     * the efile submission dashboard turns this off (matches user request to
     * drop the resize affordance from per-chart headers).
     */
    @Input()
    showResize: boolean = true;

    /**
     * Show the legacy chart-type-switcher cog icon + popover (mirrors
     * `dashboardContainerSettings.html` from `ui/app/components/dashboardExecutive/index.html`
     * and `selectChartType` in `ui/app/GlobalDirectives.js`'s
     * `gtDashboardContainerEx`). Defaults to `false` so executive admin
     * dashboards keep their existing header; the efile submission
     * dashboard opts in via `[showChartTypeSwitcher]="true"`.
     */
    @Input()
    showChartTypeSwitcher: boolean = false;

    /**
     * Legacy per-card collapse (+/− square). E-file submission dashboard keeps
     * only the Highcharts context menu (hamburger) in the header — set false.
     */
    @Input()
    showCollapseToggle: boolean = true;

    collapse: boolean = false;
    expand: boolean = false;
    popoverOpen: boolean = false;

    /**
     * Parent templates often bind `[height]="component.height"`. When the API
     * omits `height`, Angular passes `undefined`, which overrides the
     * property default and yields `{ height: undefined }` on the card — the
     * flex host collapses, `.gt-dashboard-ex-body` gets ~0px, and Highcharts
     * renders blank until the next reflow (e.g. click). Coerce to the legacy
     * default instead.
     */
    get resolvedHeight(): string {
        const h = this.height as string | undefined | null;
        if (h == null || (typeof h === 'string' && !String(h).trim())) {
            return '450px';
        }
        const s = String(h).trim().toLowerCase();
        // Percent / keyword heights collapse when the flex parent has no
        // definite block size (e-file dashboard tab), so Highcharts measures
        // ~0px until the next reflow (e.g. click). Use a concrete pixel card.
        if (
            s === 'auto' ||
            s === 'inherit' ||
            s === 'unset' ||
            s === 'initial' ||
            (s.endsWith('%') && !/px/i.test(s))
        ) {
            return '450px';
        }
        const trimmed = String(h).trim();
        // Bare number from JSON (e.g. "525") is not a valid CSS length for height.
        if (/^\d+$/.test(trimmed)) {
            return `${trimmed}px`;
        }
        return trimmed;
    }

    /**
     * Chart-type categorization mirrors `ex-chart.component.ts` so the
     * cog popover renders the same option set the chart itself supports
     * via the Highcharts hamburger menu. Keep these two lists in sync.
     */
    private readonly singleSeriesChartTypes = ['pie', 'donut', 'variablepie', 'packedbubble', 'waterfall'];
    private readonly multiSeriesChartTypes = ['line', 'spline', 'area', 'bar', 'column'];

    @HostBinding('class.gtw-ex-component-host--expanded')
    get hostExpandedClass(): boolean {
        return this.expand;
    }

    private originalPosition: { top: number; left: number } = { top: 0, left: 0 };
    private originalWidth: number = 0;
    private originalHeight: number = 0;
    private actualHeight: number = 0;

    constructor(
        private el: ElementRef,
        private eventService: ExDashboardEventService
    ) { }

    ngOnInit(): void { }

    get currentChartType(): string {
        return (this.component?.extraOptions?.componentSettings?.chart?.type || '').toLowerCase();
    }

    get chartCategory(): 'singleSeries' | 'multiSeries' | '' {
        const type = this.currentChartType;
        if (!type) return '';
        if (this.singleSeriesChartTypes.includes(type)) return 'singleSeries';
        if (this.multiSeriesChartTypes.includes(type)) return 'multiSeries';
        return '';
    }

    togglePopover(): void {
        this.popoverOpen = !this.popoverOpen;
    }

    closePopover(): void {
        if (this.popoverOpen) {
            this.popoverOpen = false;
        }
    }

    /**
     * Direct port of `selectChartType` from `gtDashboardContainerEx`
     * (`ui/app/GlobalDirectives.js` L878-891). Broadcasts the dedicated
     * `component-<id>:chartType` channel that `ex-chart.component.ts`
     * subscribes to, so the live Highcharts instance updates in-place via
     * `chart.update` (no destroy / recreate). The Highcharts hamburger menu
     * uses the same channel — chart-type switches share one path.
     */
    selectChartType(type: string): void {
        if (!this.component || !this.component.extraOptions || !this.component.extraOptions.componentSettings) {
            this.closePopover();
            return;
        }
        if (this.currentChartType === type) {
            this.closePopover();
            return;
        }
        const settings = this.component.extraOptions.componentSettings;
        settings.chart = settings.chart || {};
        settings.chart.type = type;
        this.eventService.broadcast(
            'component-' + this.component.component_id + ':chartType',
            { newType: type }
        );
        this.closePopover();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        if (!this.popoverOpen) return;
        const host = this.el.nativeElement as HTMLElement;
        const trigger = host.querySelector('.chart-type-trigger');
        if (trigger && trigger.contains(event.target as Node)) return;
        this.closePopover();
    }

    toggleCollapse(collapse: boolean) {
        if (this.collapse === collapse) {
            return;
        }

        this.collapse = collapse;
        const element = this.el.nativeElement as HTMLElement;

        if (collapse) {
            this.actualHeight = element.offsetHeight;
            element.style.transition = 'height 0.5s ease';
            element.style.height = '40px';
            element.style.overflow = 'hidden';
        } else {
            element.style.transition = 'height 0.5s ease';
            element.style.height = this.actualHeight + 'px';
            setTimeout(() => {
                element.style.overflow = '';
            }, 500);
        }
    }

    expandClose($event: any) {
        this.expand = !this.expand;
        const element = this.el.nativeElement as HTMLElement;

        if (this.expand) {
            const rect = element.getBoundingClientRect();
            this.originalPosition = { top: rect.top, left: rect.left };
            this.originalWidth = element.offsetWidth;
            this.originalHeight = element.offsetHeight;

            element.style.position = 'fixed';
            element.style.zIndex = '1020';
            element.style.boxSizing = 'border-box';
            element.style.margin = '0';
            element.style.left = this.originalPosition.left + 'px';
            element.style.top = this.originalPosition.top + 'px';
            element.style.width = this.originalWidth + 'px';
            element.style.height = this.originalHeight + 'px';
            element.style.minWidth = '0';
            element.style.transition = 'all 0.5s ease';

            requestAnimationFrame(() => {
                // Pin to the viewport (avoid width/height % on fixed — inconsistent vs inset).
                element.style.top = '0';
                element.style.left = '0';
                element.style.right = '0';
                element.style.bottom = '0';
                element.style.width = 'auto';
                element.style.height = 'auto';
            });
            element.classList.remove('collapsed');
            this.scheduleExpandedReflow();
        } else {
            element.style.transition = 'all 0.5s ease';
            element.style.height = this.originalHeight + 'px';
            element.style.width = this.originalWidth + 'px';
            element.style.left = this.originalPosition.left + 'px';
            element.style.top = this.originalPosition.top + 'px';
            element.style.right = 'auto';
            element.style.bottom = 'auto';
            element.style.maxWidth = '';
            element.style.maxHeight = '';

            setTimeout(() => {
                element.style.position = 'relative';
                element.style.zIndex = 'unset';
                element.style.top = '0';
                element.style.left = '0';
                element.style.right = '';
                element.style.bottom = '';
                element.style.maxHeight = 'unset';
                element.style.maxWidth = 'unset';
                element.style.minWidth = '100%';
                element.style.width = 'unset';
                element.style.height = this.originalHeight + 'px';
                element.style.boxSizing = '';
                element.style.margin = '';
                element.style.transition = '';
                window.dispatchEvent(new Event('resize'));
            }, 500);
            element.classList.add('collapsed');
        }
    }

    /** After the expand transition, nudge Highcharts / layout to refit to the new box. */
    private scheduleExpandedReflow(): void {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 520);
    }
}
